import {FC, useEffect, useRef, useState} from 'react'
import {Link, useSearchParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import useCancelToken from '../../../_gori/hooks/UseCancelToken'
import {toAbsoluteUrl} from '../../../_gori/helpers'
import {ROUTES} from '../../../_gori/constants'
import DownloadService from './core/_requests'

type PageState = 'loading' | 'expired'

const DownloadLayout: FC<{children: React.ReactNode}> = ({children}) => (
  <div
    className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
    style={{
      backgroundColor: '#E4EFEE',
      backgroundImage: `url(${toAbsoluteUrl('/media/gori/login/gori_bg_rocket_1.webp')})`,
      minHeight: '100vh',
    }}
  >
    <div className='container d-flex flex-center flex-column flex-column-fluid'>
      <Link to={ROUTES.LANDING.BASE} className='mb-12'>
        <img
          alt='ShipBae Logo'
          src={toAbsoluteUrl('/media/logos/big-shipbae-logo.png')}
          className='h-60px'
        />
      </Link>
      {children}
    </div>
  </div>
)

const StateCard: FC<{children: React.ReactNode}> = ({children}) => (
  <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto text-center'>
    {children}
  </div>
)

const LoadingState: FC = () => {
  const intl = useIntl()
  return (
    <StateCard>
      <div className='mb-8'>
        <span
          className='spinner-border'
          style={{width: '2.5rem', height: '2.5rem', color: '#0B6D65', borderWidth: 3}}
          role='status'
          aria-hidden='true'
        />
      </div>
      <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'DOWNLOAD_FILE_PREPARING'})}</h1>
      <div className='text-gray-400 fw-bold fs-4'>
        {intl.formatMessage({id: 'DOWNLOAD_FILE_PLEASE_WAIT'})}
      </div>
    </StateCard>
  )
}

const ExpiredState: FC = () => {
  const intl = useIntl()

  return (
    <StateCard>
      <h1 className='text-dark fw-bolder mb-8'>
        {intl.formatMessage({id: 'DOWNLOAD_FILE_EXPIRED_TITLE'})}
      </h1>

      <div
        className='rounded p-5 mb-8 fs-6 text-start fw-bold'
        style={{backgroundColor: '#FBE2E1', color: '#C0382B'}}
      >
        {intl.formatMessage({id: 'DOWNLOAD_FILE_EXPIRED_DESC'})}
      </div>

      <Link
        to={ROUTES.AUTH.LOGIN}
        className='btn btn-lg w-100 text-white fw-bold'
        style={{backgroundColor: '#0B6D65', border: 'none'}}
      >
        {intl.formatMessage({id: 'DOWNLOAD_FILE_LOGIN_BUTTON'})}
      </Link>
    </StateCard>
  )
}

const DownloadFilePage: FC = () => {
  const [searchParams] = useSearchParams()
  const {newCancelToken, isCancel} = useCancelToken()
  const hasRun = useRef(false)
  const [pageState, setPageState] = useState<PageState>('loading')

  useEffect(() => {
    const splashScreen = document.getElementById('splash-screen')
    if (splashScreen) splashScreen.style.display = 'none'
  }, [])

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    const key = searchParams.get('key')

    if (!key) {
      setPageState('expired')
      return
    }

    const initDownLoadFile = async () => {
      const config = {cancelToken: newCancelToken()}
      try {
        const result = await DownloadService.resolveKey(key, config)

        // Backend returns {status: 'not_found'} if file doesn't exist
        if (result.status === 'not_found') {
          setPageState('expired')
          return
        }

        const anchor = document.createElement('a')
        anchor.href = result.download_url
        anchor.download = result.filename
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)

        // Download started successfully, close this tab shortly after
        // so the browser has time to begin the download.
        setTimeout(() => window.close(), 1000)
      } catch (error: any) {
        if (isCancel(error)) return
        setPageState('expired')
      }
    }

    initDownLoadFile()
  }, [isCancel, newCancelToken, searchParams])

  return (
    <DownloadLayout>
      {pageState === 'loading' && <LoadingState />}
      {pageState === 'expired' && <ExpiredState />}
    </DownloadLayout>
  )
}

export {DownloadFilePage}
